import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import aulaLive from '@ublend-npm/aula-live';
import { useDispatch, useSelector, useStore } from 'react-redux';
import refreshAccessToken from '@core/actions/accessToken/refreshAccessToken.action';
import { getSocket } from '@core/actions/socket';
import { currentClassRoomId } from '@core/selectors/classroom';
import { getInitialSpaceId } from '@core/selectors/space/space.selectors';
import {
  fetchHomeUpcomingAssignments,
  HomeUpcomingAssignment,
} from '@core/assignments/api/homeUpcomingAssignments';
import { INTERCEPTOR_BLOCK_ERROR } from '@app/constants/texts';
import { HomeViewRole } from '@app/constants/home';

const FETCH_LIMIT = 50;
const LIVE_UPDATE_DEBOUNCE_MS = 2000;

const ASSIGNMENT_LIVE_EVENTS = [
  'AssessmentItem:Assignment:Saved',
  'AssessmentItem:Assignment:Deleted',
  'Handin:Assignment:Created',
  'Handin:Assignment:Updated',
  'Handin:Assignment:Deleted',
  'Assignment:Submitted',
  'Assignment:Graded',
  'Handin:Assignment:GradesReleased',
  'AssessmentItem:Assignment:GradesReleased',
] as const;

type WidgetErrorType = 'api' | 'offline';

type UseHomeUpcomingAssignmentsOptions = {
  fetchLimit?: number;
  /** Home educator/student toggle — passed to the API as `role`. */
  viewRole: HomeViewRole;
};

const isSessionBlockError = (error: unknown): boolean =>
  error instanceof Error && error.message === INTERCEPTOR_BLOCK_ERROR;

/**
 * Match Messages: only show the internet screen when the browser is actually
 * offline. Axios `Network Error` / `ERR_NETWORK` also happen after session
 * restore (stale token, blocked request) — other widgets treat those as API
 * errors, not "no internet".
 */
const getErrorType = (): WidgetErrorType => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'offline';
  }

  return 'api';
};

const useHomeUpcomingAssignments = ({
  fetchLimit = FETCH_LIMIT,
  viewRole,
}: UseHomeUpcomingAssignmentsOptions) => {
  const dispatch = useDispatch<any>();
  const store = useStore<any>();

  const spaceId = useSelector((state: any) => getInitialSpaceId(state));
  const hasInitialData = useSelector((state: any) =>
    Boolean(state.data?.hasInitialData)
  );
  const activeSpaceId = useSelector((state: any) => currentClassRoomId(state));
  const socketId = useSelector((state: any) => state.socket?.id);
  const classRoomIdsKey = useSelector((state: any) =>
    Object.keys(state.data?.classRooms || {})
      .sort()
      .join(',')
  );

  const spaceIdsToSubscribe = useMemo(() => {
    const ids = new Set<string>();

    if (classRoomIdsKey) {
      classRoomIdsKey.split(',').forEach((id) => {
        if (id) ids.add(id);
      });
    }

    if (spaceId) ids.add(spaceId);
    if (activeSpaceId) ids.add(activeSpaceId);

    return Array.from(ids);
  }, [activeSpaceId, classRoomIdsKey, spaceId]);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorType, setErrorType] = useState<WidgetErrorType | null>(null);
  const [allItems, setAllItems] = useState<HomeUpcomingAssignment[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  // Which viewRole the current items/total belong to. Used so role-toggle
  // does not briefly look "loaded" with the previous role's count.
  const [loadedRole, setLoadedRole] = useState<HomeViewRole | null>(null);
  const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(false);

  const isLoadingRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const pageRef = useRef(0);
  const totalRef = useRef(0);
  const itemsLengthRef = useRef(0);
  const viewRoleRef = useRef(viewRole);

  isLoadingRef.current = isLoading;
  pageRef.current = page;
  totalRef.current = total;
  itemsLengthRef.current = allItems.length;
  viewRoleRef.current = viewRole;

  const isLoadingForView = isLoading || loadedRole !== viewRole;
  const hasMore = allItems.length < total;

  const loadAssignments = useCallback(
    async ({ showLoading = false }: { showLoading?: boolean } = {}) => {
      if (!spaceId) {
        setAllItems([]);
        setPage(0);
        setTotal(0);
        setErrorType(null);
        if (!hasInitialData) {
          // Startup still hydrating — keep loading and do not mark this role
          // as loaded with total 0 (that locked student-only analytics at 0).
          setIsLoading(true);
          return;
        }
        // Startup finished with no spaces to query.
        setIsLoading(false);
        setLoadedRole(viewRole);
        return;
      }

      const requestRole = viewRole;

      try {
        if (showLoading) {
          setIsLoading(true);
          setErrorType(null);
        }

        const accessToken = await refreshAccessToken(
          dispatch,
          store.getState
        )({ spaceId });

        if (!accessToken) {
          if (showLoading) {
            setAllItems([]);
            setPage(0);
            setTotal(0);
            setErrorType(getErrorType());
            setLoadedRole(requestRole);
            setIsLoading(false);
          }
          return;
        }

        const { data } = await fetchHomeUpcomingAssignments({
          accessToken,
          limit: fetchLimit,
          page: 0,
          includeSubmitted: true,
          role: requestRole,
        });

        // Drop stale responses if the educator/student toggle changed mid-flight.
        if (viewRoleRef.current !== requestRole) {
          return;
        }

        const upcomingAssignments = data.upcomingAssignments || [];
        setAllItems(upcomingAssignments);
        setPage(0);
        setTotal(data.total ?? 0);
        setErrorType(null);
        setLoadedRole(requestRole);
        setHasLoadedSuccessfully(true);
      } catch (e: unknown) {
        if (viewRoleRef.current !== requestRole) {
          return;
        }

        // Session "Are you still using Aula?" dialog blocks APIs — don't
        // treat that as a widget error (Messages/Spaces keep their last UI).
        if (isSessionBlockError(e)) {
          return;
        }

        if (showLoading) {
          setAllItems([]);
          setPage(0);
          setTotal(0);
          setErrorType(getErrorType());
          setLoadedRole(requestRole);
        }
      } finally {
        if (showLoading && viewRoleRef.current === requestRole) {
          setIsLoading(false);
        }
      }
    },
    [dispatch, fetchLimit, hasInitialData, spaceId, store, viewRole]
  );

  const loadMore = useCallback(async () => {
    if (
      !spaceId ||
      isLoadingMoreRef.current ||
      isLoadingRef.current ||
      itemsLengthRef.current >= totalRef.current
    ) {
      return;
    }

    const requestRole = viewRoleRef.current;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const accessToken = await refreshAccessToken(
        dispatch,
        store.getState
      )({ spaceId });

      if (!accessToken) {
        return;
      }

      const nextPage = pageRef.current + 1;
      const { data } = await fetchHomeUpcomingAssignments({
        accessToken,
        limit: fetchLimit,
        page: nextPage,
        includeSubmitted: true,
        role: requestRole,
      });

      if (viewRoleRef.current !== requestRole) {
        return;
      }

      const nextItems = data.upcomingAssignments || [];

      if (!nextItems.length) {
        setTotal(itemsLengthRef.current);
        return;
      }

      setAllItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const uniqueNextItems = nextItems.filter(
          (item) => !existingIds.has(item.id)
        );
        return [...prev, ...uniqueNextItems];
      });
      setPage(nextPage);
      setTotal(data.total ?? totalRef.current);
    } catch {
      // Keep already-loaded items; user can scroll again to retry.
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [dispatch, fetchLimit, spaceId, store]);

  const loadAssignmentsRef = useRef(loadAssignments);
  loadAssignmentsRef.current = loadAssignments;

  const refreshAssignments = useMemo(
    () =>
      debounce(
        () => loadAssignmentsRef.current({ showLoading: false }),
        LIVE_UPDATE_DEBOUNCE_MS,
        { leading: true, trailing: true }
      ),
    []
  );

  const retry = useCallback(() => {
    return loadAssignments({ showLoading: true });
  }, [loadAssignments]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (!isMounted) return;
      await loadAssignments({ showLoading: true });
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [loadAssignments]);

  useEffect(() => {
    return () => {
      refreshAssignments.cancel();
    };
  }, [refreshAssignments]);

  useEffect(() => {
    if (!socketId || !hasLoadedSuccessfully) return undefined;

    const socket = getSocket();
    if (!socket) return undefined;

    let isCancelled = false;

    const subscribeToAllUserSpaces = async () => {
      if (!spaceIdsToSubscribe.length) return;

      const tokens = await Promise.all(
        spaceIdsToSubscribe.map(async (id) => {
          const token = await refreshAccessToken(
            dispatch,
            store.getState
          )({ spaceId: id });
          return token;
        })
      );

      if (isCancelled) return;

      tokens
        .filter(Boolean)
        .forEach((token) => aulaLive.subscribeByAccessToken(token));
    };

    const attachListeners = () => {
      ASSIGNMENT_LIVE_EVENTS.forEach((event) => {
        socket.off(event, refreshAssignments);
        socket.on(event, refreshAssignments);
      });
    };

    const handleSocketConnect = () => {
      subscribeToAllUserSpaces();
    };

    attachListeners();
    subscribeToAllUserSpaces();
    socket.on('connect', handleSocketConnect);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshAssignments();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isCancelled = true;
      socket.off('connect', handleSocketConnect);
      ASSIGNMENT_LIVE_EVENTS.forEach((event) => {
        socket.off(event, refreshAssignments);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    dispatch,
    hasLoadedSuccessfully,
    refreshAssignments,
    socketId,
    spaceIdsToSubscribe,
    store,
  ]);

  useEffect(() => {
    const handleOnline = () => {
      if (errorType === 'offline') {
        loadAssignments({ showLoading: true });
      } else {
        refreshAssignments();
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [errorType, loadAssignments, refreshAssignments]);

  return {
    allItems,
    errorType,
    isLoading: isLoadingForView,
    isLoadingMore,
    hasMore,
    total,
    loadMore,
    retry,
  };
};

export default useHomeUpcomingAssignments;
